import { useContext, useEffect, useState } from 'react';
import _ from 'lodash';
import useDataTypesFromSeriesAndMetricListItems, {
  useDataTypesFromMetricIds,
} from '../../hooks/useDataTypesFromSeriesAndMetricListItems';
import isV5ChartDef from '../../types/visTypeCheckers/isV5ChartDef';
import isSingleMetricDateMatrix from '../../types/visTypeCheckers/isSingleMetricDateMatrix';
import isDefined from '../../isDefined';
import DashboardGadgetsContext from '../../contexts/DashboardGadgetsContext';
import canvasContentTypeCheckers from '../../types/cardTypeCheckers';
import isPaceMatrix from '../../types/visTypeCheckers/isPaceMatrix';
import ReportGadgetsContext from '../../contexts/ReportGadgetsContext';

export const useDataTypesOnReportCanvas = (
  canvas: Canvas | undefined,
): string[] => {
  const [dataTypes, setDataTypes] = useState<string[]>([]);
  const { allGadgets } = useContext(DashboardGadgetsContext);
  const { allGadgets: allReportGadgets } = useContext(ReportGadgetsContext);
  const [metricIds, setMetricIds] = useState<string[]>([]);
  const [usedDefinitions, setUsedDefinitions] = useState<
    VisualisationDefinition[]
  >([]);
  const [series, setSeries] = useState<V5ChartDefinitionSeries[]>([]);
  const fromSeries = useDataTypesFromSeriesAndMetricListItems(series);
  const fromMetricIds = useDataTypesFromMetricIds(metricIds);

  useEffect(() => {
    if (!canvas) {
      setUsedDefinitions([]);
      return;
    }

    const usedDefinitions = canvas.cards
      .map((c) => {
        if (canvasContentTypeCheckers.isDashboardGadget(c)) {
          const g = allGadgets.find(
            (g) => g.id === c.content.dashboardGadgetId,
          );
          if (g) {
            return g.chartDef;
          }
          return undefined;
        } else if (canvasContentTypeCheckers.isReportGadget(c)) {
          const reportGadget = allReportGadgets.find(
            (r) => r.id === c.content.reportGadgetId,
          );
          if (reportGadget) {
            return reportGadget.chartDef;
          }
        }

        return undefined;
      })
      .filter(isDefined);
    setUsedDefinitions(usedDefinitions);
  }, [allGadgets, allReportGadgets, canvas]);

  useEffect(() => {
    const newSeries = usedDefinitions.reduce((acc, chartDef) => {
      if (chartDef && isV5ChartDef(chartDef)) {
        return [...acc, ...chartDef.series];
      } else {
        return acc;
      }
    }, [] as V5ChartDefinitionSeries[]);
    setSeries(newSeries);
  }, [usedDefinitions]);

  useEffect(() => {
    const metricIds = usedDefinitions
      .map((d) => {
        if (isSingleMetricDateMatrix(d) || isPaceMatrix(d)) {
          return d.metricId;
        }
        return undefined;
      })
      .filter(isDefined);

    setMetricIds(metricIds);
  }, [usedDefinitions]);

  useEffect(() => {
    const dataTypeFromGrids = usedDefinitions.reduce((acc, chartDef) => {
      if (
        chartDef &&
        isV5ChartDef(chartDef) &&
        chartDef.gadgetType === 'simpleGrid' &&
        chartDef.simpleGridConfig
      ) {
        return [...acc, chartDef.simpleGridConfig.dataType];
      }
      return acc;
    }, [] as string[]);

    setDataTypes(
      _.uniq([...fromSeries, ...dataTypeFromGrids, ...fromMetricIds]),
    );
  }, [fromMetricIds, fromSeries, usedDefinitions]);

  return dataTypes;
};

export default useDataTypesOnReportCanvas;
